import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {volumeModeOptions} from '@/modules/drug-catalog/constants'
import {useGetSolutionDrugs} from '@/modules/drug-catalog/solutions/api'
import {useGetPatient} from '@/modules/patients/api/use-get-patient'
import {useGetPatientCharacteristics} from '@/modules/patients/api/use-get-patient-characteristics'
import {useGetPatientLatestGfrLab} from '@/modules/patients/api/use-get-patient-latest-gfr-lab'
import {useGetPatientTherapies} from '@/modules/patients/api/use-get-patient-therapies'
import {useGetTherapySequenceSiblings} from '@/modules/patients/api/use-get-therapy-sequence-siblings'
import {getParameterDefaultValuesNew, mapPatientProtocolsToSelectItems} from '@/modules/patients/functions'
import {getParameterSchemaNew, ParameterSchemaNew} from '@/modules/patients/schemas'
import {useGetSettings} from '@/modules/settings/api'
import {useGetInfiniteDoctors} from '@/modules/users/api'
import {DrugLimitCheckResult} from '@/shared/contexts'
// concentration preview hidden until the consolidated branch ships
// import {PreparationConcentrationHint} from './preparation-concentration-hint'
// import {usePreparationConcentrationDisplay, type MedicineFormValues} from './use-preparation-concentration'
import {usePrescriptionRoles} from '@/shared/hooks/use-permissions'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {parseDate} from '@ark-ui/react/date-picker'
import {Alert, Box, Button, Grid, HStack, Text} from '@chakra-ui/react'
import {DoseCalcModeActive, OverdosePreventionLevel, PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {PatientProtocol, PatientTherapy, SolutionType} from '@cypro/engine/queries'
import {BasicDataResources} from '@cypro/engine/schema'
import {
  addDaysUtc,
  canEditPatientTherapy,
  canEditValidatedPrescription,
  computeCycleDayFromTherapyDate,
  computeTherapyDateFromCycleDay,
  daysBetweenTimestamps,
  getFirstCycleStartDate,
} from '@cypro/engine/utils'
import * as sdk from '@cypro/server/sdk'
import {dialog, DialogBody, FormSection} from '@cypro/ui/components'
import {Tooltip} from '@cypro/ui/components/ui/tooltip'
import {useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {revalidateLogic, useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {useEffect, useRef, useState} from 'react'
import {LuCirclePlus, LuTrash2} from 'react-icons/lu'
import {SequenceValidationPanels} from '../validation/validation-panels'
import {DrugFormNew} from './drug-form-new'
import {doseCalculationUsesPatientValues, doseFieldsChanged} from './drug-form-total'
import {OverdoseWarningDialog} from './overdose-warning-dialog'
import {ShiftFutureSequencesDialog, ShiftFutureSequenceSibling} from './shift-future-sequences-dialog'
import {useDoctorPrescribedMedicines} from './use-doctor-prescribed-medicines'
import {closeAddParameterDialog} from './util'

// A sibling can be date-shifted until the pharmacy validates it (locked afterwards),
// mirroring canShiftTherapySequence in engine/src/mutators/patient/update-therapy-parameter.ts.
function canShiftSibling(sibling: {status?: string | null; pharmacistValidatedAt?: number | null}): boolean {
  return (
    sibling.status === PatientTherapySequenceStatus.enum.pending_approval ||
    sibling.status === PatientTherapySequenceStatus.enum.pending_doctor_validation ||
    (sibling.status === PatientTherapySequenceStatus.enum.validating && !sibling.pharmacistValidatedAt)
  )
}

export interface AddParameterDialogProps {
  patientProtocols: PatientProtocol[]
  patientId: string
  existing?: PatientTherapy
  duplicate?: boolean
}

export function AddParameterDialogNew({
  patientProtocols,
  existing: openedWith,
  patientId,
  duplicate = false,
}: AddParameterDialogProps) {
  const {t} = useTranslate('common')
  // follow the live row, so removing a validation from the pop-up unlocks Save without
  // closing it; a touched form keeps its values when the defaults change
  const [patientTherapies] = useGetPatientTherapies(openedWith ? patientId : undefined)
  const existing = (openedWith && patientTherapies?.find(therapy => therapy.id === openedWith.id)) || openedWith
  const zero = useZero()
  const {settings} = useGetSettings()
  const [reachedMaxDose, setReachedMaxDose] = useState<DrugLimitCheckResult[]>([])
  const [disableSubmit, setDisableSubmit] = useState('')
  const [editedAfterValidation, setEditedAfterValidation] = useState(false)
  const pendingValidationActionRef = useRef<(() => Promise<void>) | null>(null)
  const protocolItems = mapPatientProtocolsToSelectItems(patientProtocols)
  const [characteristics] = useGetPatientCharacteristics(patientId)
  const [gfr] = useGetPatientLatestGfrLab(patientId)
  const [therapySequenceSiblings] = useGetTherapySequenceSiblings(
    existing?.patientProtocolId ?? undefined,
    existing?.id
  )
  const isSyncingDateWithCycleDayRef = useRef(false)
  const {solutions: carrierSolutions} = useGetSolutionDrugs({filters: {types: [SolutionType.enum.CARRIER_SOLUTION]}})
  const {items: applicationForms} = useGetBasicDataItems({resource: BasicDataResources.applicationForms})
  const volumeModeSelectItems = volumeModeOptions.map(item => ({label: t(item), value: item}))

  const infiniteResponsibleDoctors = useGetInfiniteDoctors()
  // read the patient here so a new prescription prefills the responsible doctor from the About section
  const [patient] = useGetPatient(patientId)

  // mirrors the PHARMACIST_VALIDATE guard (isPharmacist, status validating, not yet pharmacy-validated) —
  // the pharmacist opens the prescription editor to confirm the dispensed product, not to re-prescribe
  const roles = usePrescriptionRoles()
  const {canValidatePharmacist, canEdit} = roles

  const isPharmacistValidating =
    Boolean(existing) &&
    !duplicate &&
    existing?.status === PatientTherapySequenceStatus.enum.validating &&
    !existing?.pharmacistValidatedAt &&
    canValidatePharmacist

  const defaultValues = getParameterDefaultValuesNew(
    duplicate,
    existing,
    settings?.dayZeroProtocol,
    isPharmacistValidating || Boolean(settings?.prescribeByTradeName),
    patient?.responsibleDoctorId
  )

  const doctorPrescribed = useDoctorPrescribedMedicines(existing)

  const form = useAppForm({
    defaultValues: defaultValues as ParameterSchemaNew,
    validators: {
      onSubmit: getParameterSchemaNew(patientProtocols),
    },
    listeners: {
      async onChange({formApi}) {
        const values = formApi.state.values
        const {data} = await sdk.postPatientsCanAddTherapy({
          body: {
            ...(existing?.id && !duplicate && {id: existing.id}),
            patientId,
            patientProtocolId: values.patientProtocolId,
            fixedDate: values.fixedDate?.toDate('UTC').getTime(),
            day: values.day,
            medicine: values.medicine.map(m => ({
              activeSubstanceId: m.activeSubstanceId,
              prescribedNameId: m.prescribedNameId,
              calculationMethod: m.calculationMethod,
              regularDose: m.regularDose,
              precisionDecimals: m.precisionDecimals,
              doseProportion: m.doseProportion,
            })),
          },
        })

        setReachedMaxDose(data?.reachedDoses || [])
        setDisableSubmit(data?.errorMessage || '')
      },
      onChangeDebounceMs: 100,
    },
    validationLogic: revalidateLogic(),
    onSubmit(props) {
      const medicine = props.value.medicine.map((m, index) => ({
        ...(m.id && {id: m.id}),
        // a substance-only prescription keeps the doctor's substance when the pharmacist has not picked a product yet
        activeSubstanceId: (m.activeSubstanceId ?? existing?.medicine?.[index]?.activeSubstanceId) as string,
        prescribedNameId: m.prescribedNameId,
        calculationMethod: m.calculationMethod as DoseCalcModeActive,
        regularDose: m.regularDose,
        precisionDecimals: m.precisionDecimals,
        doseProportion: m.doseProportion,
      }))
      const values = {
        patientId,
        patientProtocolId: props.value.patientProtocolId,
        responsibleDoctorId: props.value.responsibleDoctorId || null,
        fixedDate: props.value.fixedDate?.toDate('UTC').getTime(),
        cycle: props.value.cycle,
        day: props.value.day,
        time: props.value.time,
        carrierSolutionId: props.value.carrierSolutionId,
        applicationFormId: props.value.applicationFormId,
        duration: props.value.duration,
        volume: props.value.volume,
        volumeMode: props.value.volumeMode,
        medicine,
        therapyNote: props.value.therapyNote,
        pharmacyNote: props.value.pharmacyNote,
        autIdem: props.value.autIdem,
        recurringCycle: props.value.recurringCycle,
      }

      if (existing?.id && !duplicate) {
        return onSubmitPrescription({...values, id: existing.id})
      }

      return onSubmitPrescription(values)
    },
  })

  type TherapyParameterPayload =
    | Parameters<typeof zero.mutators.patients.addTherapyParameterNew>[0]
    | Parameters<typeof zero.mutators.patients.updateTherapySequenceNew>[0]

  function onSubmitPrescription(payload: TherapyParameterPayload) {
    // editing a doctor-validated prescription can revert its validation; notes, carrier, volume and volume mode keep it
    if (existing?.id && !duplicate && existing.status === PatientTherapySequenceStatus.enum.validating) {
      setEditedAfterValidation(true)
    }

    const isExistingProtocolUpdate =
      'id' in payload && payload.id && !duplicate && existing && Boolean(payload.patientProtocolId)
    // Cycle/day are computed from the picked date, so a changed date shows up as a changed cycle/day.
    const dateChanged = existing && (payload.cycle !== existing.cycle || payload.day !== existing.day)

    if (isExistingProtocolUpdate && existing?.patientProtocolId && dateChanged) {
      const patientProtocol = patientProtocols.find(pp => pp.id === payload.patientProtocolId)
      const daysRepetition = patientProtocol?.protocol?.daysRepetition
      const startDate = patientProtocol?.startDate

      if (daysRepetition && startDate && patientProtocol) {
        const dayZeroProtocol = Boolean(settings?.dayZeroProtocol)
        const requestedCycle = payload.cycle ?? existing.cycle ?? 1
        const requestedDay = payload.day ?? (dayZeroProtocol ? 0 : 1)
        const newTherapyDate = computeTherapyDateFromCycleDay(
          patientProtocol,
          requestedCycle,
          requestedDay,
          daysRepetition,
          dayZeroProtocol
        )
        const deltaDays = daysBetweenTimestamps(existing.therapyDate, newTherapyDate)

        // Everything scheduled for this protocol assignment on or after the day being moved -
        // any drug, not just this one's own future cycles - shifts by the same number of days.
        const siblingsPreview: ShiftFutureSequenceSibling[] = (therapySequenceSiblings || [])
          .filter(sibling => sibling.therapyDate >= existing.therapyDate && canShiftSibling(sibling))
          .map(sibling => ({
            id: sibling.id,
            currentDate: sibling.therapyDate,
            newDate: addDaysUtc(sibling.therapyDate, deltaDays),
            willLoseValidation: sibling.status === PatientTherapySequenceStatus.enum.validating,
          }))

        if (siblingsPreview.length > 0) {
          return dialog.open('shift-future-sequences', {
            children: (
              <ShiftFutureSequencesDialog
                onConfirm={shift => runOverdoseCheckThenSubmit({...payload, shiftFutureSequences: shift})}
              />
            ),
          })
        }
      }
    }

    return runOverdoseCheckThenSubmit(payload)
  }

  async function runOverdoseCheckThenSubmit(payload: TherapyParameterPayload) {
    const {data} = await sdk.postPatientsCanAddTherapy({
      body: {
        ...('id' in payload && payload.id && {id: payload.id}),
        patientId,
        patientProtocolId: payload.patientProtocolId,
        fixedDate: payload.fixedDate,
        day: payload.day,
        medicine: payload.medicine.map(m => ({
          activeSubstanceId: m.activeSubstanceId,
          prescribedNameId: m.prescribedNameId,
          calculationMethod: m.calculationMethod,
          regularDose: m.regularDose,
          precisionDecimals: m.precisionDecimals,
          doseProportion: m.doseProportion,
        })),
      },
    })

    if (data?.errorMessage) {
      setDisableSubmit(data.errorMessage)
      pendingValidationActionRef.current = null
      toaster.error({title: t(data.errorMessage)})
      return false
    }

    const reachedMaxDoses = data?.reachedDoses || []
    const hasWarning = reachedMaxDoses.find(
      item => item.overdosePreventionLevel === OverdosePreventionLevel.enum.SOFT_WARNING
    )
    const hasHardStop = reachedMaxDoses.find(
      item => item.overdosePreventionLevel === OverdosePreventionLevel.enum.HARD_STOP
    )

    if (hasHardStop && hasHardStop.overdosePreventionLevel) {
      pendingValidationActionRef.current = null
      return dialog.open('overdose-warning', {
        children: (
          <OverdoseWarningDialog
            name={hasHardStop.substanceName}
            limit={hasHardStop.limit}
            overdosePreventionLevel={hasHardStop.overdosePreventionLevel}
            unit={hasHardStop.unit}
            onSubmit={() => submitTherapyParameter(payload)}
          />
        ),
      })
    }

    if (hasWarning && hasWarning.overdosePreventionLevel) {
      return dialog.open('overdose-warning', {
        children: (
          <OverdoseWarningDialog
            name={hasWarning.substanceName}
            limit={hasWarning.limit}
            unit={hasWarning.unit}
            overdosePreventionLevel={hasWarning.overdosePreventionLevel}
            // the warning the prescriber read and proceeded past is logged with the prescription
            onSubmit={() =>
              submitTherapyParameter({
                ...payload,
                doseLimitConfirmed: [
                  {
                    activeSubstanceId: hasWarning.substanceId,
                    limit: hasWarning.limit,
                    unit: hasWarning.unit,
                    level: hasWarning.level,
                  },
                ],
              })
            }
            onCancel={() => {
              pendingValidationActionRef.current = null
            }}
          />
        ),
      })
    }

    return submitTherapyParameter(payload)
  }

  async function submitTherapyParameter(payload: TherapyParameterPayload) {
    const isUpdate = 'id' in payload && Boolean(payload.id)
    const saved = isUpdate
      ? await zero.attemptMutation(zero.mutators.patients.updateTherapySequenceNew(payload), async mutation => {
          const data = await mutation.server
          return Boolean(data)
        })
      : await zero.attemptMutation(zero.mutators.patients.addTherapyParameterNew(payload), async mutation => {
          const data = await mutation.server
          return Boolean(data)
        })

    if (!saved) {
      pendingValidationActionRef.current = null
      return false
    }

    const validate = pendingValidationActionRef.current
    if (validate) {
      pendingValidationActionRef.current = null
      await validate()
      return true
    }

    toaster.success({
      title: t('success'),
      description: t(isUpdate ? 'therapy_parameter_updated_successfully' : 'therapy_parameter_created_successfully'),
    })
    form.reset()
    closeAddParameterDialog()
    return true
  }

  function onAddSecondDrug() {
    form.pushFieldValue('medicine', {
      activeSubstanceId: undefined,
      prescribedNameId: undefined,
      calculationMethod: undefined,
      regularDose: 0,
      precisionDecimals: 2,
      doseProportion: 100,
    })
  }

  function onSubmit() {
    form.handleSubmit()
  }

  const protocol = useStore(form.store, state => state.values.patientProtocolId)
  const selectedPatientProtocol = patientProtocols.find(pp => pp.id === protocol)
  const dayZeroProtocol = Boolean(settings?.dayZeroProtocol)

  function onFixedDateChange(value: ParameterSchemaNew['fixedDate']) {
    if (isSyncingDateWithCycleDayRef.current || !value || !selectedPatientProtocol?.protocol) return
    const ms = value.toDate('UTC').getTime()
    const {cycle, day} = computeCycleDayFromTherapyDate(
      selectedPatientProtocol,
      ms,
      selectedPatientProtocol.protocol.daysRepetition,
      dayZeroProtocol
    )
    isSyncingDateWithCycleDayRef.current = true
    form.setFieldValue('cycle', cycle, {dontRunListeners: true, dontUpdateMeta: true})
    form.setFieldValue('day', day, {dontRunListeners: true, dontUpdateMeta: true})
    isSyncingDateWithCycleDayRef.current = false
  }

  // Cycle/day are read-only and derived from the date, so compute them for a prefilled date
  // (duplicate/edit) and whenever the protocol or day-zero setting they depend on changes.
  useEffect(() => {
    onFixedDateChange(form.getFieldValue('fixedDate'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientProtocol, dayZeroProtocol])

  // The patient row can still be loading when the form initializes, so prefill the responsible
  // doctor once it arrives - unless a value is already set or the user has picked one.
  useEffect(() => {
    if (existing || !patient?.responsibleDoctorId) return
    if (form.getFieldValue('responsibleDoctorId')) return
    form.setFieldValue('responsibleDoctorId', patient.responsibleDoctorId, {
      dontRunListeners: true,
      dontUpdateMeta: true,
    })
  }, [existing, patient?.responsibleDoctorId, form])

  const requireCarrierSolution = useStore(form.store, state => state.values.requireCarrierSolution)
  const carrierSolutionId = useStore(form.store, state => state.values.carrierSolutionId)
  const showPrescribingNames = useStore(form.store, state => state.values.showPrescribingNames)
  const autIdem = useStore(form.store, state => state.values.autIdem)
  const drugCount = useStore(form.store, state => state.values.medicine?.length || 0)
  // const concentrationDisplay = usePreparationConcentrationDisplay(
  //   (formMedicines as MedicineFormValues[]) ?? [],
  //   {carrierVolumeMl: carrierVolume as number | undefined, volumeMode: formVolumeMode, characteristics, gfr}
  // )
  // a pharmacist without the "May edit validated prescriptions" permission may only change what
  // production needs; everything the doctor validated stays locked
  const isClinicalFieldReadOnly = isPharmacistValidating && !canEditValidatedPrescription(roles)
  const isSubmitting = useStore(form.store, state => state.isSubmitting)
  // The doctor may edit the prescription until the pharmacist validates it.
  const isLockedAfterValidation =
    Boolean(existing) &&
    !duplicate &&
    existing?.status !== PatientTherapySequenceStatus.enum.pending_approval &&
    existing?.status !== PatientTherapySequenceStatus.enum.pending_doctor_validation &&
    !(existing?.status === PatientTherapySequenceStatus.enum.validating && !existing?.pharmacistValidatedAt)
  // a doctor-validated prescription keeps its validated totals until a dose field is edited
  const showSavedTotalAmount =
    Boolean(existing) &&
    !duplicate &&
    existing?.status !== PatientTherapySequenceStatus.enum.pending_approval &&
    existing?.status !== PatientTherapySequenceStatus.enum.pending_doctor_validation
  const medicineValues = useStore(form.store, state => state.values.medicine)
  // a changed dose makes the dialog calculate with the patient's current values, so say so
  const doseRecalculationUsesCurrentValues =
    showSavedTotalAmount &&
    (medicineValues ?? []).some((medicine, index) => {
      const saved = existing?.medicine?.[index]
      if (!saved || !doseCalculationUsesPatientValues(medicine.calculationMethod)) return false
      return doseFieldsChanged(medicine, {
        activeSubstanceId: saved.activeSubstanceId,
        calculationMethod: saved.calculationMethod,
        regularDose: saved.regularDose,
        doseProportion: saved.doseProportion,
        precisionDecimals: saved.precisionDecimals,
      })
    })

  const isReadOnly = !canEditPatientTherapy(roles)

  const isSaveDisabled = isSubmitting || isLockedAfterValidation || isReadOnly || Boolean(disableSubmit)

  function getSaveDisableReason() {
    if (isReadOnly) return 'therapy_sequence.read_only'
    if (isLockedAfterValidation) return 'therapy_sequence.locked_after_validation'
    return disableSubmit
  }
  const saveDisableReason = getSaveDisableReason()

  // Runs the normal save pipeline (validators, can-add checks, overdose/shift dialogs, event log),
  // then the validation action once the save actually succeeded.
  function saveThenValidate(validate: () => Promise<void>) {
    if (isSaveDisabled) {
      if (saveDisableReason) toaster.error({title: t(saveDisableReason)})
      return
    }

    pendingValidationActionRef.current = validate
    void Promise.resolve(form.handleSubmit()).finally(() => {
      if (pendingValidationActionRef.current === validate) {
        pendingValidationActionRef.current = null
      }
    })
  }

  const saveButton = (
    <Button onClick={() => onSubmit()} loading={isSubmitting} disabled={isSaveDisabled}>
      {t('save_changes')}
    </Button>
  )

  return (
    <DialogBody padding="0">
      <form onSubmit={onSubmit} style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto'}}>
        <form.AppForm>
          <Grid gridTemplateColumns="27% 73%" gap="0" flex="1" height="max-content">
            <Box
              backgroundColor="bg.muted"
              overflow="hidden"
              padding="6"
              borderRightWidth="1px"
              borderRightStyle="solid"
              borderRightColor="border"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box spaceY="8">
                <FormSection
                  label={t(existing && !duplicate ? 'therapy_sequence.edit.title' : 'therapy_sequence.add.title')}
                  description={t('planning_and_scheduling_details')}
                >
                  <form.AppField name="responsibleDoctorId" mode="value">
                    {field => (
                      <field.InfiniteCombobox
                        size="sm"
                        label={t('doctor_responsible', 'Doctor (responsible)')}
                        placeholder={t('eg', {value: t('name')})}
                        contentProps={{zIndex: 2000}}
                        disabled={isReadOnly || isClinicalFieldReadOnly}
                        {...infiniteResponsibleDoctors}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="patientProtocolId" mode="value">
                    {field => (
                      <field.Select
                        size="sm"
                        label={t('protocol')}
                        placeholder={t('select_protocol')}
                        items={protocolItems}
                        contentProps={{zIndex: 2000}}
                        displayClearTrigger
                        emptyMessage={t('no_protocols_assigned')}
                        disabled={isReadOnly || isClinicalFieldReadOnly}
                      />
                    )}
                  </form.AppField>
                  <form.AppField
                    name="fixedDate"
                    mode="value"
                    listeners={{onChange: ({value}) => onFixedDateChange(value)}}
                  >
                    {field => (
                      <field.DatePicker
                        size="sm"
                        label={t('date')}
                        required
                        minDate={
                          selectedPatientProtocol
                            ? parseDate(new Date(getFirstCycleStartDate(selectedPatientProtocol)))
                            : undefined
                        }
                        disabled={isReadOnly || isClinicalFieldReadOnly}
                      />
                    )}
                  </form.AppField>

                  <form.AppField name="time">
                    {field => (
                      <field.HourInput
                        label={t('therapy_sequence.time')}
                        placeholder="13:45"
                        required
                        disabled={isReadOnly || isClinicalFieldReadOnly}
                      />
                    )}
                  </form.AppField>

                  {protocol && (
                    <>
                      <HStack width={'100%'} alignItems="flex-start">
                        <form.AppField name="cycle">
                          {field => (
                            <field.NumberInput
                              size="sm"
                              label={t('protocol.cycle')}
                              placeholder="1"
                              min={1}
                              required
                              disabled
                              errorMessagePadding={24}
                            />
                          )}
                        </form.AppField>
                        <form.AppField name="day">
                          {field => (
                            <field.NumberInput
                              size="sm"
                              label={t('therapy_sequence.day')}
                              placeholder="0"
                              required
                              disabled
                            />
                          )}
                        </form.AppField>
                      </HStack>
                      {!existing && (
                        <form.AppField name="recurringCycle">
                          {field => (
                            <field.CheckBox
                              label={t('recurring_cycle.title')}
                              description={t('recurring_cycle.description')}
                              disabled={isReadOnly}
                            />
                          )}
                        </form.AppField>
                      )}
                    </>
                  )}
                </FormSection>
              </Box>

              {!duplicate && existing && (
                <Box key={existing.id}>
                  <SequenceValidationPanels
                    existing={existing}
                    form={form}
                    editedAfterValidation={editedAfterValidation}
                    isSaveDisabled={isSaveDisabled}
                    saveDisabledReason={saveDisableReason}
                    onSaveAndValidate={saveThenValidate}
                  />
                </Box>
              )}
            </Box>

            <Box padding="6" display="flex" flexDir="column" gap="4" overflowY="auto">
              <Box flex="1" minHeight="0" display="flex" flexDirection="column">
                <FormSection
                  spaceY="4"
                  label={
                    <HStack width="100%" justifyContent="space-between">
                      {t('medication')}

                      {canEdit && (
                        <HStack whiteSpace="nowrap">
                          {showPrescribingNames && settings?.autIdemLogic && (
                            <form.AppField name="autIdem">
                              {field => (
                                <field.Switch
                                  label={t('aut_idem')}
                                  colorPalette={'cyan'}
                                  isLabelOnLeft
                                  disabled={isReadOnly || isClinicalFieldReadOnly}
                                />
                              )}
                            </form.AppField>
                          )}

                          <form.AppField
                            name="showPrescribingNames"
                            listeners={{
                              onChange: ({value}) => {
                                if (isReadOnly) return
                                if (!value) {
                                  form.setFieldValue('autIdem', value)
                                }
                                form.setFieldValue('medicine[0].activeSubstanceId', undefined)
                                form.setFieldValue('medicine[0].prescribedNameId', undefined)
                                if (drugCount >= 2) {
                                  form.setFieldValue('medicine[1].activeSubstanceId', undefined)
                                  form.setFieldValue('medicine[1].prescribedNameId', undefined)
                                }
                              },
                            }}
                          >
                            {field => (
                              <field.Switch
                                label={t('therapy_sequence.medicine.show_prescribed_names')}
                                colorPalette={'cyan'}
                                isLabelOnLeft
                                disabled={isReadOnly || isClinicalFieldReadOnly}
                              />
                            )}
                          </form.AppField>
                        </HStack>
                      )}
                    </HStack>
                  }
                  description={t('therapy_data_dosage')}
                >
                  {Boolean(existing?.doctorValidatedAt) &&
                    doctorPrescribed
                      .filter(prescribed => prescribed.name)
                      .map((prescribed, index) => (
                        <Text key={existing?.medicine?.[index]?.id ?? index} textStyle="sm" fontWeight="medium">
                          {t('prescribed_by_doctor')}:{' '}
                          {prescribed.isTradeName
                            ? `${prescribed.name} ${t('prescribed_as_trade_name')}`
                            : `${prescribed.name} ${t('prescribed_as_active_substance')} · ${prescribed.dosage}`}
                        </Text>
                      ))}

                  <Box display="flex" flexDirection={'row'} width="100%" gap="5" flex="1" minHeight="0">
                    <form.Field name="medicine" mode="array">
                      {field =>
                        field?.state?.value?.map((_medicine, i) => {
                          const key = `medicine-${i}`
                          return (
                            <DrugFormNew
                              key={key}
                              index={i}
                              hasMultiple={drugCount > 1}
                              form={form}
                              fields={`medicine[${i}]`}
                              characteristics={characteristics}
                              gfr={gfr}
                              limits={reachedMaxDose}
                              usePrescribedName={showPrescribingNames}
                              useSavedTotalAmount={showSavedTotalAmount}
                              pharmacistValidation={isPharmacistValidating}
                              savedMedicine={existing?.medicine?.[i]}
                              disabled={isReadOnly}
                              lockedClinicalFields={isClinicalFieldReadOnly}
                              autIdem={autIdem}
                            />
                          )
                        })
                      }
                    </form.Field>
                  </Box>
                </FormSection>

                {doseRecalculationUsesCurrentValues && (
                  <Alert.Root status="warning" borderRadius="md" mt="8">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>{t('therapy_sequence.medicine.dose_changed_title')}</Alert.Title>
                      <Alert.Description>{t('therapy_sequence.medicine.dose_changed_recalculation')}</Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                )}

                <FormSection mt="8" spaceY="4" flexShrink="0" label={t('therapy_sequence.administration_carrier')}>
                  <Grid gridTemplateColumns="1fr 1fr" gapX="4" gapY="4">
                    <form.AppField
                      name="applicationFormId"
                      mode="value"
                      listeners={{
                        onChange: ({value}) => {
                          const selected = applicationForms.find(item => item.id === value)
                          form.setFieldValue('requireCarrierSolution', selected?.carrierRequired || false)
                        },
                      }}
                    >
                      {field => (
                        <field.Select
                          size="sm"
                          label={t('therapy_sequence.application_form.label')}
                          placeholder={t('select')}
                          items={applicationForms.map(item => ({label: item.name, value: item.id}))}
                          portalled
                          required
                          disabled={isReadOnly || isClinicalFieldReadOnly}
                        />
                      )}
                    </form.AppField>
                    <form.AppField name="duration">
                      {field => (
                        <field.NumberInput
                          size="sm"
                          label={t('therapy_sequence.duration.label')}
                          placeholder={t('therapy_sequence.duration.placeholder')}
                          min={0}
                          disabled={isReadOnly || isClinicalFieldReadOnly}
                        />
                      )}
                    </form.AppField>
                    <form.AppField name="carrierSolutionId" mode="value">
                      {field => (
                        <field.Select
                          size="sm"
                          label={t('therapy_sequence.carrier_solution.label')}
                          placeholder={t('select')}
                          items={carrierSolutions.map(item => ({label: item.name, value: item.id}))}
                          portalled
                          required={requireCarrierSolution}
                          disabled={isReadOnly}
                        />
                      )}
                    </form.AppField>

                    <Box width={'100%'} display="grid" gridTemplateColumns="1fr 1fr" gap="4">
                      <form.AppField name="volumeMode" mode="value">
                        {field => (
                          <field.Select
                            size="sm"
                            label={t('method')}
                            placeholder={t('select')}
                            items={volumeModeSelectItems}
                            disabled={!carrierSolutionId || isReadOnly}
                            required={Boolean(carrierSolutionId)}
                            portalled
                          />
                        )}
                      </form.AppField>
                      <form.AppField name="volume">
                        {field => (
                          <field.NumberInput
                            size="sm"
                            label={t('therapy_sequence.volume.label')}
                            placeholder={t('therapy_sequence.volume.placeholder')}
                            min={0}
                            disabled={!carrierSolutionId || isReadOnly}
                            required={Boolean(carrierSolutionId)}
                          />
                        )}
                      </form.AppField>
                    </Box>

                    {/* concentration preview hidden until the consolidated branch ships
                    {concentrationDisplay && (
                      <Box gridColumn="1 / -1">
                        <PreparationConcentrationHint display={concentrationDisplay} />
                      </Box>
                    )} */}

                    <form.AppField name="therapyNote">
                      {field => (
                        <field.Textarea
                          label={t('therapy_note')}
                          placeholder={t('therapy_note')}
                          rows={4}
                          bgColor="white"
                          helpText={t('therapy_note.visibility')}
                          disabled={isReadOnly}
                        />
                      )}
                    </form.AppField>

                    <form.AppField name="pharmacyNote">
                      {field => (
                        <field.Textarea
                          label={t('pharmacy_note')}
                          placeholder={t('pharmacy_note')}
                          rows={4}
                          bgColor="white"
                          helpText={t('pharmacy_note.visibility')}
                          disabled={isReadOnly}
                        />
                      )}
                    </form.AppField>
                  </Grid>
                </FormSection>
              </Box>

              <Box spaceY="4" mt="4">
                <Box display={'flex'} flexDir={'row'} justifyContent={'space-between'}>
                  <Box>
                    {!isReadOnly && drugCount >= 2 && canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        colorPalette={'red'}
                        onClick={() => form.removeFieldValue('medicine', 1)}
                      >
                        <LuTrash2 />
                        {t('remove_second_medication')}
                      </Button>
                    )}
                    {!isReadOnly && drugCount < 2 && canEdit && (
                      <Button size="sm" variant="ghost" colorPalette={'cyan'} onClick={onAddSecondDrug}>
                        <LuCirclePlus />
                        {t('add_second_medication')}
                      </Button>
                    )}
                  </Box>

                  <Box display={'flex'} gap={4}>
                    <Button variant="outline" onClick={() => closeAddParameterDialog()}>
                      {t('close')}
                    </Button>
                    {saveDisableReason ? <Tooltip content={t(saveDisableReason)}>{saveButton}</Tooltip> : saveButton}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </form.AppForm>
      </form>
    </DialogBody>
  )
}
